import { useState, useCallback, useEffect, useRef } from "react";
import type { ThemeMode } from "../../../shared/types";
import { APP_VERSION } from "../../../shared/version";
import { AppearanceSection } from "./AppearanceSection";
import { CategoriesSection } from "./CategoriesSection";
import { ToolsSection } from "./ToolsSection";
import { AdvancedModeSection } from "./AdvancedModeSection";
import { AccountSection } from "./AccountSection";
import { TokensSection } from "./TokensSection";
import { NotificationsSection } from "./NotificationsSection";
import { SectionsSection } from "./SectionsSection";
import { GallerySection } from "./GallerySection";
import { ReleaseNotesModal } from "./ReleaseNotesModal";
import { XIcon, ChevronRight, PaletteIcon, TagIcon, HammerIcon, KeyIcon, FlaskIcon, UserIcon, BellIcon, LayoutGridIcon, ImageIcon } from "../../../ui/icons";
import { useModals } from "../../../hooks/useModals";
import { useAppState } from "../../../hooks/useAppState";

type SettingsSection = "appearance" | "categories" | "sections" | "tools" | "notifications" | "gallery" | "tokens" | "account" | "advanced";

interface SectionConfig {
  readonly id: SettingsSection;
  readonly label: string;
  readonly description: string;
  readonly icon: React.ComponentType<{ readonly className?: string }>;
}

const SECTIONS: ReadonlyArray<SectionConfig> = [
  { id: "appearance", label: "Apparence", description: "Thème et couleurs", icon: PaletteIcon },
  { id: "categories", label: "Catégories", description: "Gérez vos catégories", icon: TagIcon },
  { id: "tools", label: "Outils", description: "Timers et productivité", icon: HammerIcon },
  { id: "notifications", label: "Notifications", description: "Rappels et alertes", icon: BellIcon },
  { id: "sections", label: "Sections", description: "Masquer des pages", icon: LayoutGridIcon },
  { id: "gallery", label: "Galerie", description: "Gérez vos images", icon: ImageIcon },
  { id: "tokens", label: "Tokens API", description: "Accès CLI et intégrations", icon: KeyIcon },
  { id: "advanced", label: "Avancé", description: "Fonctionnalités expérimentales", icon: FlaskIcon },
  { id: "account", label: "Compte", description: "Vos informations", icon: UserIcon },
] as const;

interface AccordionItemProps {
  readonly section: SectionConfig;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly children: React.ReactNode;
}

function AccordionItem({ section, isOpen, onToggle, children }: AccordionItemProps) {
  const Icon = section.icon;

  return (
    <div data-section={section.id} className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-surface-alt/50 transition-colors"
        aria-expanded={isOpen}
      >
        <Icon className="w-5 h-5 text-ink-muted shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ink">{section.label}</h3>
          <p className="text-xs text-ink-muted truncate">{section.description}</p>
        </div>
        <ChevronRight
          className={`w-5 h-5 text-ink-muted transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-4 pt-0">{children}</div>
      </div>
    </div>
  );
}

interface SettingsPanelProps {
  readonly theme: ThemeMode;
  readonly hasFocusTask: boolean;
  readonly onThemeChange: (theme: ThemeMode) => void;
  readonly onImport?: (data: { tasks: unknown[]; events: unknown[]; habits?: unknown[] }) => void;
  readonly notificationPermission?: boolean;
  readonly onRequestNotificationPermission?: () => void;
}

export function SettingsPanel({
  theme,
  hasFocusTask,
  onThemeChange,
  onImport,
  notificationPermission = false,
  onRequestNotificationPermission,
}: SettingsPanelProps) {
  const modals = useModals();
  const appState = useAppState();
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [openSections, setOpenSections] = useState<ReadonlySet<SettingsSection>>(() => {
    const saved = localStorage.getItem("palnia_settings_open_sections");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as string[];
        return new Set(parsed.filter((id): id is SettingsSection =>
          SECTIONS.some((s) => s.id === id),
        ));
      } catch { /* ignore */ }
    }
    return appState.settingsCollapsed ? new Set() : new Set(SECTIONS.map((s) => s.id));
  });

  // Sync openSections when settingsCollapsed changes
  useEffect(() => {
    const next = appState.settingsCollapsed
      ? new Set<SettingsSection>()
      : new Set(SECTIONS.map((s) => s.id));
    setOpenSections(next);
    localStorage.removeItem("palnia_settings_open_sections");
  }, [appState.settingsCollapsed]);

  const toggleSection = useCallback((sectionId: SettingsSection) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      localStorage.setItem("palnia_settings_open_sections", JSON.stringify([...newSet]));
      return newSet;
    });
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTarget = modals.settingsScrollTarget;

  useEffect(() => {
    if (!scrollTarget) return;
    modals.setSettingsScrollTarget(null);

    const sectionMap: Record<string, SettingsSection> = {
      security: "account",
      "connected-accounts": "account",
    };
    const section = sectionMap[scrollTarget];
    if (section) {
      setOpenSections((prev) => {
        const newSet = new Set(prev);
        newSet.add(section);
        localStorage.setItem("palnia_settings_open_sections", JSON.stringify([...newSet]));
        return newSet;
      });
      requestAnimationFrame(() => {
        const el = scrollRef.current?.querySelector(`[data-section="${section}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume target once on mount
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") modals.setShowSettings(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modals]);

  const onClose = () => modals.setShowSettings(false);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40 animate-fade-in backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Fullscreen Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-6"
        onClick={onClose}
      >
        <div
          className="relative w-full h-full md:h-auto md:max-h-[85vh] md:max-w-2xl bg-surface md:rounded-lg shadow-2xl flex flex-col overflow-hidden animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="flex items-center justify-between px-6 py-5 border-b border-border shrink-0 pt-[calc(1.25rem+env(safe-area-inset-top))]">
            <div>
              <h2 className="text-lg font-bold text-ink">Paramètres</h2>
              <p className="text-xs text-ink-muted mt-0.5">
                Personnalisez votre expérience
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-surface-alt text-ink-muted touch-feedback transition-colors"
              aria-label="Fermer les paramètres"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </header>

          {/* Scrollable Content */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-custom">
            {SECTIONS.map((section) => {
              const isOpen = openSections.has(section.id);

              return (
                <AccordionItem
                  key={section.id}
                  section={section}
                  isOpen={isOpen}
                  onToggle={() => toggleSection(section.id)}
                >
                  {section.id === "appearance" && (
                    <AppearanceSection theme={theme} onThemeChange={onThemeChange} showHolidays={appState.showHolidays} onToggleHolidays={appState.toggleHolidays} />
                  )}
                  {section.id === "categories" && <CategoriesSection />}
                  {section.id === "sections" && (
                    <SectionsSection
                      hiddenSections={appState.hiddenSections}
                      advancedMode={appState.advancedMode}
                      onToggleSection={appState.toggleSection}
                    />
                  )}
                  {section.id === "tools" && (
                    <ToolsSection
                      hasFocusTask={hasFocusTask}
                      showPomodoro={appState.showPomodoro}
                      showTwenty={appState.showTwenty}
                      showStopwatch={appState.showStopwatch}
                      onPomodoro={appState.togglePomodoro}
                      onTwenty={appState.toggleTwenty}
                      onStopwatch={appState.toggleStopwatch}
                      onFocusMode={() => { modals.setShowFocus(true); onClose(); }}
                      onTimerSettings={() => modals.setShowTimerSettings(true)}
                      onAchievements={() => modals.setShowAchievements(true)}
                      onTutorial={() => { modals.setShowTutorial(true); onClose(); }}
                    />
                  )}
                  {section.id === "notifications" && (
                    <NotificationsSection
                      permissionGranted={notificationPermission}
                      onRequestPermission={onRequestNotificationPermission ?? (() => {})}
                    />
                  )}
                  {section.id === "gallery" && <GallerySection />}
                  {section.id === "tokens" && <TokensSection />}
                  {section.id === "account" && (
                    <AccountSection
                      onAdmin={() => { modals.setShowAdmin(true); onClose(); }}
                      onImport={onImport}
                      autoOpenRegenerate={scrollTarget === "security"}
                    />
                  )}
                  {section.id === "advanced" && (
                    <AdvancedModeSection
                      advancedMode={appState.advancedMode}
                      onAdvancedMode={appState.toggleAdvancedMode}
                      googleCalendarUi={appState.googleCalendarUi}
                      onGoogleCalendarUi={appState.toggleGoogleCalendarUi}
                      settingsCollapsed={appState.settingsCollapsed}
                      onSettingsCollapsed={appState.toggleSettingsCollapsed}
                      desktopBottomBar={appState.desktopBottomBar}
                      onDesktopBottomBar={appState.toggleDesktopBottomBar}
                    />
                  )}
                </AccordionItem>
              );
            })}
          </div>

          {/* Footer */}
          <footer className="px-6 py-3 border-t border-border shrink-0">
            <p className="text-[10px] text-ink-faint text-center">
              Palnia v{APP_VERSION}
              {" · "}
              <button
                type="button"
                onClick={() => setShowReleaseNotes(true)}
                className="text-accent hover:text-accent/80 transition-colors"
              >
                Nouveautés
              </button>
              {" · "}
              <a
                href="/roadmap"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent/80 transition-colors"
                title="Votez pour les prochaines fonctionnalités"
              >
                Roadmap
              </a>
            </p>
          </footer>

          {showReleaseNotes && (
            <ReleaseNotesModal onClose={() => setShowReleaseNotes(false)} />
          )}
        </div>
      </div>
    </>
  );
}
